import { LoaderFunction, useLoaderData, useNavigate } from "react-router";
import { AviTracerApi } from "../../../avitracerApi";
import { EntityDisplayInfo } from "../../../components/EntityDetails/EntityDetailsDisplayInfo";
import EntityDetailsPage from "../../../components/EntityDetails/EntityDetailsPage"
import Flight from "../../../models/flight"
import { useAppDispatch } from "../../../store/hooks";
import { createFlight, deleteFlight, editFlight } from "../../../store/flightSlice";
import moment from "moment";
import { useSearchParams } from "react-router-dom";
import { AirfieldSlotType } from "../../../models/airfieldSlots";
import { database } from "../../../firebase";
import { ref, set } from "firebase/database";
import { useCallback, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import ReverseFlightModal from "./ReverseFlightModal";
import DuplicateFlightModal from "./DuplicateFlightModal";
import { fetchSwitchingTeamIfNeeded } from "../../../utils/fetchSwitchingTeam";

export const flightLoader: LoaderFunction = async (params) => {
  if (params.params["flightId"]) {
      return fetchSwitchingTeamIfNeeded((orgId) => AviTracerApi.getFlightWithId(params.params["flightId"]!, orgId));
  }

  return null;
};

interface EditFlightPageProps {
  rulesMode?: "vfr" | "ifr";
}

function EditFlightPage({ rulesMode }: EditFlightPageProps){
  const dispatch = useAppDispatch()
  const navigate = useNavigate();
  const [showReverseModal, setShowReverseModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);

  const [searchParams] = useSearchParams();
  
  const searchParamDepartureDateTime = useMemo(() => {
    return searchParams.has("departureDateTime") ? moment((Number(searchParams.get("departureDateTime")!)) * 1000) : undefined;
  },[searchParams])
 
  const airfieldSlots: Flight["airfieldSlots"] = useMemo(() => {
    if (searchParams.has("depAirfieldId") && searchParams.has("departureDateTime") && searchParams.has("slotType") && searchParams.has("bookedSlotKey")) {
      return {
        departure: {
          airfieldId: searchParams.get("depAirfieldId")!,
          startDateTimeEpoch: Number(searchParams.get("departureDateTime")!),
          slotType: searchParams.get("slotType")! as AirfieldSlotType,
          bookedSlotKey: searchParams.get("bookedSlotKey")!,    
        },
      }
    }
    return undefined;
  },[searchParams]);

  const loadedFlight = useLoaderData() as Flight;

  // The form runs in one rules mode: from the route for new flights, from the flight itself on edit.
  const effectiveRulesMode: "vfr" | "ifr" =
    loadedFlight ? (loadedFlight.flightRules === "V" ? "vfr" : "ifr") : rulesMode ?? "vfr";

  //The chooser's "Mixed" card presets Y/Z via ?rules= — still the IFR flow, just different rules.
  const presetRules = (searchParams.get("rules") ?? "").toUpperCase();

  const flight: Flight = loadedFlight ?? {
    departureDateTime: searchParamDepartureDateTime,
    airfieldSlots,
    flightRules: effectiveRulesMode === "ifr" ? ((presetRules === "Y" || presetRules === "Z" ? presetRules : "I") as Flight["flightRules"]) : "V",
    //IFR flights start with NO route (customRoute undefined) — the Route section shows just the route
    //selector (pick a saved IFR route, or "Custom Route" to build one). Choosing either fills in the
    //DEP/DEST step below, exactly like the VFR saved-route → custom-route flow.
  };

  const deletePendingSlotIfExists = useCallback(() => {
    if (searchParamDepartureDateTime && searchParams.has("depAirfieldId") && searchParams.has("pendingSlotKey")){
      const slotDatePath = `/airfield/${searchParams.get("depAirfieldId")}/slots/${moment(searchParamDepartureDateTime).format("DD-MM-YYYY")}`
      const refToDelete = ref(database, `${slotDatePath}/${searchParams.get("pendingSlotKey")}`)
      set(refToDelete, null)
    }
  }, [searchParams, searchParamDepartureDateTime])

  const handleSaveFlight = async (flight: Flight) => {
    if (!flight.id) {
      return await dispatch(createFlight(flight)).unwrap()
    }else{
      return await dispatch(editFlight(flight)).unwrap()
    }
  };

  const handleDeleteFlight = async (flight: Flight) => {
    await dispatch(deleteFlight(flight)).unwrap()
  };

  const goBack = () => {
    deletePendingSlotIfExists()

    navigate("/flights")
  }

  const hasRoute = loadedFlight && (loadedFlight.route || loadedFlight.customRoute);

  if (showReverseModal && loadedFlight) {
    return (
      <ReverseFlightModal flight={loadedFlight} onClose={() => setShowReverseModal(false)} />
    );
  }

  if (showDuplicateModal && loadedFlight) {
    return (
      <DuplicateFlightModal flight={loadedFlight} onClose={() => setShowDuplicateModal(false)} />
    );
  }

  return (
    <EntityDetailsPage
      entity={flight}
      handleClose={goBack}
      displayInfo={EntityDisplayInfo.Flight(flight.aircraft?.fuelUnit, effectiveRulesMode)}
      dispatchSaveEntity={handleSaveFlight}
      dispatchDeleteEntity={handleDeleteFlight}
      extraHeaderContent={
        loadedFlight ? (
          <>
            <Button variant="outline-dark" size="sm" onClick={() => setShowDuplicateModal(true)}>
              Duplicate
            </Button>
            {hasRoute && (
              <Button variant="outline-dark" size="sm" className="ms-2" onClick={() => setShowReverseModal(true)}>
                Reverse
              </Button>
            )}
          </>
        ) : undefined
      }
    />
  );
}

export default EditFlightPage